// authService.js - Centralized authentication service with account setup endpoints

// Store CSRF token and auth state
let csrfToken = localStorage.getItem('csrf_token') || null;
let isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
let currentUser = null;

try {
  const userJson = localStorage.getItem('currentUser');
  if (userJson) {
    currentUser = JSON.parse(userJson);
  }
} catch (e) {
  console.error('Error parsing user data from localStorage', e);
}

// Base URL
const BASE_URL = 'https://bank.wells.finance/api/v1';

/**
 * Make API request with proper credentials and CSRF token handling
 * @param {string} endpoint - API endpoint path
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @param {object} body - Request body for POST/PUT requests
 */
export const apiRequest = async (endpoint, method = 'GET', body = null) => {
  const url = `${BASE_URL}${endpoint}`;
  const options = {
    method,
    credentials: 'include', // Critical for sending cookies with cross-origin requests
    headers: {
      'Content-Type': 'application/json',
    }
  };

  // Add body with CSRF token for non-GET requests
  if (body && (method === 'POST' || method === 'PUT')) {
    // Clone body to avoid modifying the original
    const requestBody = { ...body };
    
    // Add CSRF token if available
    if (csrfToken) {
      requestBody.csrf_token = csrfToken;
    }
    
    options.body = JSON.stringify(requestBody);
  }

  try {
    console.log(`Making ${method} request to ${endpoint}`);
    const response = await fetch(url, options);
    
    // Handle 401 Unauthorized before parsing JSON
    if (response.status === 401) {
      console.error('401 Unauthorized - Redirecting to login...');
      
      // Clear all auth data
      isAuthenticated = false;
      currentUser = null;
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('currentUser');
      
      // Redirect to login
      window.location.href = '/login';
      
      // Throw error to stop execution
      throw new Error('Authentication required');
    }
    
    const data = await response.json();
    
    // Check for authentication errors in response data
    if (!response.ok && data?.message === 'Authentication required') {
      console.error('Authentication required - Redirecting to login...');
      
      // Clear all auth data
      isAuthenticated = false;
      currentUser = null;
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('currentUser');
      
      // Redirect to login
      window.location.href = '/login';
      
      // Throw error to stop execution
      throw new Error('Authentication required');
    }
    
    // Update CSRF token if present in response
    if (data?.data?.csrf_token) {
      csrfToken = data.data.csrf_token;
      localStorage.setItem('csrf_token', csrfToken);
      console.log('CSRF token updated:', csrfToken);
    }

    // Handle authentication state updates based on endpoint
    if (endpoint === '/auth/user') {
      isAuthenticated = data?.data?.authenticated || false;
      currentUser = data?.data?.user || null;
      
      // Always update localStorage with current auth state
      localStorage.setItem('isAuthenticated', isAuthenticated.toString());
      if (currentUser) {
        localStorage.setItem('currentUser', JSON.stringify(currentUser));
      } else {
        localStorage.removeItem('currentUser');
      }
      
      console.log('Auth state updated from /auth/user:', { isAuthenticated, hasUser: !!currentUser });
    } else if (endpoint === '/auth/verify' && data.success) {
      // Set authenticated state after OTP verification
      isAuthenticated = true;
      currentUser = data?.data?.user || null;
      
      // Update localStorage
      localStorage.setItem('isAuthenticated', 'true');
      if (currentUser) {
        localStorage.setItem('currentUser', JSON.stringify(currentUser));
      }
      
      console.log('Auth state updated from /auth/verify:', { isAuthenticated, hasUser: !!currentUser });
    } else if (endpoint === '/auth/register' && data.success) {
      // Set authenticated state after registration
      isAuthenticated = true;
      currentUser = data?.data?.user || null;
      
      // Update localStorage
      localStorage.setItem('isAuthenticated', 'true');
      if (currentUser) {
        localStorage.setItem('currentUser', JSON.stringify(currentUser));
      }
      
      console.log('Auth state updated from /auth/register:', { isAuthenticated, hasUser: !!currentUser });
    } else if (endpoint === '/auth/logout' && data.success) {
      // Clear auth state after logout
      isAuthenticated = false;
      currentUser = null;
      
      // Update localStorage
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('currentUser');
      // Don't remove CSRF token as it might be needed for future logins
      
      console.log('Auth state cleared after logout');
    }

    return data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

/**
 * Get current authentication state
 */
export const getAuthState = () => {
  // Ensure state from localStorage is always in sync with our module variables
  const storedIsAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  if (storedIsAuthenticated !== isAuthenticated) {
    console.log('Syncing auth state with localStorage', { 
      was: isAuthenticated, 
      now: storedIsAuthenticated 
    });
    isAuthenticated = storedIsAuthenticated;
  }
  
  // Try to get user from localStorage if not in memory
  if (!currentUser && localStorage.getItem('currentUser')) {
    try {
      currentUser = JSON.parse(localStorage.getItem('currentUser'));
      console.log('Restored user data from localStorage');
    } catch (e) {
      console.error('Error parsing user data', e);
    }
  }
  
  return {
    isAuthenticated,
    currentUser,
    csrfToken
  };
};

/**
 * Manually set authentication state (useful for direct state management)
 */
export const setAuthState = (state) => {
  if (typeof state.isAuthenticated === 'boolean') {
    isAuthenticated = state.isAuthenticated;
    localStorage.setItem('isAuthenticated', isAuthenticated.toString());
  }
  
  if (state.currentUser) {
    currentUser = state.currentUser;
    localStorage.setItem('currentUser', JSON.stringify(currentUser));
  } else if (state.currentUser === null) {
    currentUser = null;
    localStorage.removeItem('currentUser');
  }
  
  console.log('Auth state manually updated:', { isAuthenticated, hasUser: !!currentUser });
};

/**
 * Check if user is authenticated (makes API call)
 */
export const checkAuth = async () => {
  try {
    const response = await apiRequest('/auth/user', 'GET');
    return response.data?.authenticated || false;
  } catch (error) {
    console.error('Auth check failed:', error);
    return false;
  }
};

/**
 * Login with email to get OTP
 * @param {string} email - User's email
 */
export const login = async (email) => {
  try {
    const response = await apiRequest('/auth/login', 'POST', { email });
    return response;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

/**
 * Verify OTP code
 * @param {string} email - User's email
 * @param {string} otp - OTP code
 */
export const verifyOTP = async (email, otp) => {
  try {
    const response = await apiRequest('/auth/verify', 'POST', { email, otp });
    
    // Set local auth state immediately on success
    if (response.success) {
      setAuthState({
        isAuthenticated: true,
        currentUser: response.data?.user || null
      });
    }
    
    return response;
  } catch (error) {
    console.error('OTP verification failed:', error);
    throw error;
  }
};

/**
 * Complete registration for new users
 * @param {object} profileData - User profile data
 */
export const register = async (profileData) => {
  try {
    const response = await apiRequest('/auth/register', 'POST', profileData);
    
    // Set local auth state immediately on success
    if (response.success) {
      setAuthState({
        isAuthenticated: true,
        currentUser: response.data?.user || null
      });
    }
    
    return response;
  } catch (error) {
    console.error('Registration failed:', error);
    throw error;
  }
};

/**
 * Logout user
 */
export const logout = async () => {
  try {
    const response = await apiRequest('/auth/logout', 'GET');
    
    // Clear local auth state immediately on success
    if (response.success) {
      setAuthState({
        isAuthenticated: false,
        currentUser: null
      });
    }
    
    return response;
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
};

/**
 * Update user's Bitget UID
 * @param {string} uid - User's Bitget UID
 */
export const updateUid = async (uid) => {
  try {
    const response = await apiRequest('/auth/update-uid', 'POST', { uid });
    
    if (response.success && response.data?.user) {
      // Update local user data
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const updatedUser = { ...currentUser, ...response.data.user };
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    }
    
    return response;
  } catch (error) {
    console.error('Update UID failed:', error);
    throw error;
  }
};

/**
 * Update and verify API credentials
 * @param {object} apiData - API credentials
 */
export const updateApiCredentials = async (apiData) => {
  try {
    const response = await apiRequest('/auth/update-api', 'POST', apiData);
    
    if (response.success && response.data?.user) {
      // Update local user data
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const updatedUser = { ...currentUser, ...response.data.user };
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      
      // Update authentication state if user is now active (role > 0)
      if (response.data.user.role > 0) {
        localStorage.setItem('isAuthenticated', 'true');
      }
    }
    
    return response;
  } catch (error) {
    console.error('Update API credentials failed:', error);
    throw error;
  }
};

// Export a default object with all auth methods
export default {
  apiRequest,
  getAuthState,
  setAuthState,
  checkAuth,
  login,
  verifyOTP,
  register,
  logout,
  updateUid,
  updateApiCredentials
};